import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '../hooks/useNotifications'
import type { AppNotification } from '../types'

/** Libellé d'une notification, déduit de son type et de ses données. */
function useLabel() {
  const { t } = useTranslation()

  return (notification: AppNotification): string => {
    if (notification.type === 'ParentalAlertNotification') {
      return t(`notifications.parental.${String(notification.data.type)}`, {
        name: String(notification.data.child_name ?? ''),
        defaultValue: t('notifications.generic'),
      })
    }

    return t('notifications.generic')
  }
}

export function NotificationBell() {
  const { t, i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const { data } = useNotifications()
  const markRead = useMarkNotificationRead()
  const markAll = useMarkAllNotificationsRead()
  const label = useLabel()

  const unread = data?.unread_count ?? 0
  const notifications = data?.notifications ?? []

  const formatDate = (iso: string | null) =>
    iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso)) : ''

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-label={t('notifications.open', { count: unread })}
        className="relative flex h-11 w-11 items-center justify-center rounded-full text-on-surface-variant hover:bg-surface-low"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
          <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5S10.5 3.17 10.5 4v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
        </svg>

        {unread > 0 && (
          <span className="absolute right-1.5 top-1.5 min-w-4 rounded-full bg-mindeia-error px-1 text-[10px] font-bold leading-4 text-white">
            {unread}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 z-30 mt-1 w-80 max-w-[85vw] rounded-2xl border border-outline-variant bg-white p-3 shadow-xl">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-on-surface">{t('notifications.title')}</p>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                className="text-xs font-medium text-primary hover:underline"
              >
                {t('notifications.mark_all')}
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-on-surface-variant">
              {t('notifications.empty')}
            </p>
          ) : (
            <ul className="flex max-h-80 flex-col gap-1 overflow-y-auto">
              {notifications.map((notification) => (
                <li key={notification.id}>
                  <button
                    type="button"
                    onClick={() => markRead.mutate(notification.id)}
                    className={[
                      'flex w-full flex-col gap-0.5 rounded-xl px-3 py-2 text-left transition-colors hover:bg-surface-low',
                      notification.read ? '' : 'bg-primary/5',
                    ].join(' ')}
                  >
                    <span className="text-sm text-on-surface">{label(notification)}</span>
                    <span className="text-[11px] text-on-surface-variant">
                      {formatDate(notification.created_at)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
